/* eslint-disable complexity, @typescript-eslint/no-unsafe-member-access,  @typescript-eslint/no-unsafe-assignment */
import { BUTTON_SIZES } from '@elliemae/ds-button-v2';
import { ChevronDoubleLeft, ChevronDoubleRight, ChevronLeft, ChevronRight } from '@elliemae/ds-icons';
import { useCallback, useContext, useMemo } from 'react';
import { ControlledDateTimePickerDatatestid } from '../../../constants/index.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import { getMonthNameByMonthNumber } from '../../../utils/dateHelpers.js';
import { CalendarContext } from './CalendarContext.js';
import { StyledHeader, StyledHeaderButton, StyledHeaderLabel } from './Styleds.js';

const useGetFormattedCurrentDate = () => {
  const { currentMonth, currentYear } = useContext(CalendarContext);

  const monthString = currentMonth >= 0 && currentMonth <= 11 ? `${getMonthNameByMonthNumber(currentMonth)} ` : '';

  return useMemo(() => `${monthString}${currentYear}`, [monthString, currentYear]);
};
export const CalendarHead = (): JSX.Element => {
  const currentFormatedDate = useGetFormattedCurrentDate();
  const {
    handlePrevYear,
    handlePrevMonth,
    handleNextMonth,
    handleNextYear,
    handlePrevYearKeyDown,
    handlePrevMonthKeyDown,
    handleNextMonthKeyDown,
    handleNextYearKeyDown,
    onPrevYearFocus,
    onPrevMonthFocus,
    onNextMonthFocus,
    onNextYearFocus,
  } = useContext(CalendarContext);

  const {
    isControllerOnly,
    autoFocusPrevMonthArrow,
    prevYearBtnRef,
    prevMonthBtnRef,
    nextMonthBtnRef,
    nextYearBtnRef,
    latestInteractionRegion,
    currFocusDescriber,
    getProps,
  } = useContext(ControlledDateTimePickerContext);

  const handlePrevYearRef = useCallback(
    (ButtonDomNode: HTMLButtonElement) => {
      prevYearBtnRef.current = ButtonDomNode;
      if (latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-prev-year')
        ButtonDomNode?.focus?.();
    },
    [currFocusDescriber, latestInteractionRegion, prevYearBtnRef],
  );
  const handlePrevMonthRef = useCallback(
    (ButtonDomNode: HTMLButtonElement) => {
      prevMonthBtnRef.current = ButtonDomNode;
      if (latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-prev-month')
        ButtonDomNode?.focus?.();
    },
    [currFocusDescriber, latestInteractionRegion, prevMonthBtnRef],
  );
  const handleNextMonthRef = useCallback(
    (ButtonDomNode: HTMLButtonElement) => {
      nextMonthBtnRef.current = ButtonDomNode;
      if (latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-next-month')
        ButtonDomNode?.focus?.();
    },
    [currFocusDescriber, latestInteractionRegion, nextMonthBtnRef],
  );
  const handleNextYearRef = useCallback(
    (ButtonDomNode: HTMLButtonElement) => {
      nextYearBtnRef.current = ButtonDomNode;
      if (latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-next-year')
        ButtonDomNode?.focus?.();
    },
    [currFocusDescriber, latestInteractionRegion, nextYearBtnRef],
  );

  return (
    <StyledHeader>
      <StyledHeaderButton
        aria-label="previous year"
        buttonType="raw"
        size={BUTTON_SIZES.M}
        onClick={handlePrevYear}
        innerRef={handlePrevYearRef}
        onKeyDown={handlePrevYearKeyDown}
        onFocus={onPrevYearFocus}
        tabIndex={isControllerOnly ? -1 : 0}
        data-testid={ControlledDateTimePickerDatatestid.CALENDAR.PREV_YEAR}
        type="button"
        getOwnerProps={getProps}
      >
        <ChevronDoubleLeft height={14} width={14} fill="#25292f" />
      </StyledHeaderButton>
      <StyledHeaderButton
        autoFocus={autoFocusPrevMonthArrow}
        aria-label="previous month"
        buttonType="raw"
        size={BUTTON_SIZES.M}
        onClick={handlePrevMonth}
        innerRef={handlePrevMonthRef}
        onKeyDown={handlePrevMonthKeyDown}
        onFocus={onPrevMonthFocus}
        data-testid={ControlledDateTimePickerDatatestid.CALENDAR.PREV_MONTH}
        type="button"
        getOwnerProps={getProps}
        // this is a tabstop for when the calendar is open
      >
        <ChevronLeft height={14} width={14} fill="#25292f" />
      </StyledHeaderButton>
      <StyledHeaderLabel
        aria-live="polite"
        data-testid={ControlledDateTimePickerDatatestid.CALENDAR.HEADER_LABEL}
        getOwnerProps={getProps}
      >
        {currentFormatedDate}
      </StyledHeaderLabel>
      <StyledHeaderButton
        aria-label="next month"
        buttonType="raw"
        size={BUTTON_SIZES.M}
        onClick={handleNextMonth}
        innerRef={handleNextMonthRef}
        onKeyDown={handleNextMonthKeyDown}
        onFocus={onNextMonthFocus}
        data-testid={ControlledDateTimePickerDatatestid.CALENDAR.NEXT_MONTH}
        type="button"
        getOwnerProps={getProps}
      >
        <ChevronRight height={14} width={14} fill="#25292f" />
      </StyledHeaderButton>
      <StyledHeaderButton
        aria-label="next year"
        buttonType="raw"
        size={BUTTON_SIZES.M}
        onClick={handleNextYear}
        innerRef={handleNextYearRef}
        onKeyDown={handleNextYearKeyDown}
        onFocus={onNextYearFocus}
        data-testid={ControlledDateTimePickerDatatestid.CALENDAR.NEXT_YEAR}
        type="button"
        getOwnerProps={getProps}
      >
        <ChevronDoubleRight height={14} width={14} fill="#25292f" />
      </StyledHeaderButton>
    </StyledHeader>
  );
};
